import React, { useMemo, useCallback } from "react";
import debounce from "lodash/debounce";
import cs from "classnames";
import CreatableSelect from "react-select/creatable";
import { components } from "react-select";
import { Spinner } from "reactstrap";

import { useBookingsContext } from "../../Context/BookingsContext";
import { useDirectorySearch } from "../../../../lib/hooks/useDirectorySearch";
import { useCalendarContext } from "../../../../lib/context/Calendar/CalendarContext";
import { isValidEmail } from "../../../../lib/helpers/isValidEmail";

import { Button } from "../../../shared/Button/Button";
import { SearchUserCustomOption } from "../../../shared/Forms/SearchUserInput/SearchUserCustomOption";
import { CustomNoOptionsMessage } from "../../../shared/NoOptionsMessage/NoOptionsMessage";

interface Props {
  handleDoneButton?: (e: { label: string; value: string }[]) => void;
}

export const CreatablePeopleList = ({ handleDoneButton }: Props) => {
  const { attendees, handleAttendees } = useBookingsContext();
  const { personalCalendar } = useCalendarContext();

  const {
    handleDirectorySearch,
    handleIntegrationSearch,
    searchError,
    searchResults,
    setSearchResults,
    searching,
  } = useDirectorySearch();

  // Debounced search implementation
  const debouncedSearch = useCallback(
    debounce((searchQuery: string) => {
      if (searchQuery && searchQuery.length > 1) {
        handleDirectorySearch(searchQuery);
      }
    }, 500),
    [personalCalendar]
  );

  const handleInputChange = (inputValue: string) => {
    if (inputValue && inputValue.length > 1) {
      debouncedSearch(inputValue);
    }
  };

  // Custom menu list to add the loader at the top
  const MenuList = (props: any) => {
    const hasResult = props.options.some(
      (option: any) =>
        (option.value
          .toLowerCase()
          .includes(props.selectProps.inputValue.toLowerCase()) ||
          option.label
            .toLowerCase()
            .includes(props.selectProps.inputValue.toLowerCase())) &&
        !option?.__isNew__
    );

    return (
      <components.MenuList {...props}>
        {!hasResult &&
          !searching &&
          isValidEmail(props?.selectProps?.inputValue || "") && (
            <div className="SearchingDirectory" style={{ height: "40px" }}>
              No result found
            </div>
          )}
        {searching && (
          <div
            className={cs("SearchingDirectory", {
              "pt-min": !isValidEmail(props?.selectProps?.inputValue || ""),
            })}
            style={{
              height: "40px",
            }}
          >
            <Spinner size="sm" color="primary" className="mr-2" />
            {searching}
          </div>
        )}
        {searchResults?.people && searchResults.people?.length > 8 ? (
          <div className="SearchingDirectory SearchingDirectory__narrow">
            Type more to narrow you search...
          </div>
        ) : (
          props.children
        )}

        {!!personalCalendar.length &&
          props.selectProps.inputValue?.length > 1 && (
            <div
              className="SearchDirectory"
              style={{
                cursor: searching ? "not-allowed" : "pointer",
                opacity: searching ? 0.5 : 1,
              }}
              onMouseDown={(e) => {
                if (searching) return;
                e.preventDefault();
                handleIntegrationSearch(props.selectProps.inputValue);
              }}
            >
              <span>Search directory</span>
            </div>
          )}
      </components.MenuList>
    );
  };

  const options = useMemo(() => {
    if (!searchResults?.people?.length) return [];

    return searchResults.people.map((item) => ({
      value: item.email,
      label: item.displayName,
    }));
  }, [searchResults]);

  const selectedValues = useMemo(() => {
    return attendees.map((attendee) => ({
      label: attendee,
      value: attendee,
    }));
  }, [attendees]);

  return (
    <>
      {searchError ? (
        <div>Error fetching people, please try again.</div>
      ) : (
        <>
          <CreatableSelect
            isMulti
            className={cs(
              "SearchWorkplaceForm__createableOptions NoOption PeopleSelect",
              {
                "SearchWorkplaceForm__createableOptions--multiple":
                  selectedValues.length >= 2,
              }
            )}
            classNamePrefix="AttendeesSelect"
            onChange={(e) => {
              if (e === null) {
                return;
              }
              const values = e.map((item) => item.value);
              handleAttendees(values);
            }}
            onInputChange={handleInputChange}
            value={selectedValues}
            onMenuClose={() => {
              if (searchResults?.people?.length) {
                setSearchResults(undefined);
              }
            }}
            options={options}
            isValidNewOption={(input) => isValidEmail(input)}
            placeholder="Add attendees"
            formatCreateLabel={(inputValue) =>
              isValidEmail(inputValue)
                ? `Use this address: "${inputValue}"`
                : null
            }
            components={{
              MenuList,
              Option: SearchUserCustomOption,
              NoOptionsMessage: (props) => (
                <CustomNoOptionsMessage {...props} hideMessage={!!searching} />
              ),
            }}
          />
        </>
      )}

      <span className="d-block mb-3 text-14 text-secondary font-weight-light max-w-none-important">
        Choose from the directory or manually enter an external email
      </span>

      {handleDoneButton && (
        <Button
          title="Done"
          size="small"
          color="outline-primary"
          className="Button__extra-small"
          onClick={() => {
            handleDoneButton(selectedValues);
          }}
        />
      )}
    </>
  );
};
